import { IconMikrofon } from "../icons/index.js";
import { KropkaNaZywo } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { ProgressBar } from "../ui/ProgressBar.js";
import { StanBledu } from "../ui/stany.js";
import type { RecorderState } from "../../hooks/useMicRecorder.js";

interface Props {
  state: RecorderState;
  level: number;
  f0Hz: number | null;
  elapsedMs: number;
  maxSeconds: number;
  /**
   * Okno modelu wymiarow, gdy jest krotsze niz limit nagrywania. null = milczymy.
   *
   * Bez tego zdania limit odkrywalo sie dopiero po nagraniu, z odbitej proby:
   * mikrofon zapraszal na 15 s, model wymiarowy slucha 12 s i kazde dluzsze
   * nagranie wracalo bez trzech liczb (zgloszenie z 23.08.2026).
   */
  oknoWymiarowS?: number | null;
  pitchTrack: Array<number | null>;
  onStart: () => void;
  onStop: () => void;
  /** Ile trwa analiza. Zero, gdy nie trwa. */
  analizaMs?: number;
  /** Przerwanie czekania na serwer. Bez tego jedynym wyjsciem bylo przeladowanie strony. */
  onPrzerwij?: () => void;
}

/** KP-2: przecinek dziesietny, bez zbednego zera po kropce. */
function liczbaPl(w: number): string {
  return (Number.isInteger(w) ? String(w) : w.toFixed(1)).replace(".", ",");
}

/** Ile sekund przed twardym limitem zaczynamy ostrzegac. */
const WARN_BEFORE_SEC = 3;

const PITCH_VIEW_MIN_HZ = 70;
const PITCH_VIEW_MAX_HZ = 400;
const PITCH_POINTS = 60;

/** Uklad wspolrzednych wyspy. Szerokosc w procentach, wysokosc w pikselach. */
const VIEW_W = 100;
const VIEW_H = 64;

/**
 * Ikona zatrzymania. icons.html nie ma jej w kanonicznym zestawie, wiec rysujemy
 * ja w tym samym jezyku co reszta (IC-1: obrys 2px w skali 24, zaokraglenia,
 * currentColor). Brak w zestawie zgloszony orkiestratorowi.
 */
function IkonaStop({ size = 18 }: { size?: number }): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

/** Ciagle odcinki linii tonu. Cisza i szept daja `null`, czyli przerwe w linii. */
function odcinki(track: Array<number | null>): string[] {
  const out: string[] = [];
  let biezacy: string[] = [];

  track.forEach((hz, i) => {
    if (hz === null) {
      if (biezacy.length > 1) out.push(biezacy.join(" "));
      biezacy = [];
      return;
    }
    const x = (i / Math.max(1, PITCH_POINTS - 1)) * VIEW_W;
    const ograniczony = Math.max(PITCH_VIEW_MIN_HZ, Math.min(PITCH_VIEW_MAX_HZ, hz));
    // Skala logarytmiczna, bo slyszymy wysokosc w poltonach, nie w hercach.
    const udzial =
      Math.log2(ograniczony / PITCH_VIEW_MIN_HZ) / Math.log2(PITCH_VIEW_MAX_HZ / PITCH_VIEW_MIN_HZ);
    biezacy.push(`${x.toFixed(2)},${(VIEW_H - udzial * VIEW_H).toFixed(2)}`);
  });

  if (biezacy.length > 1) out.push(biezacy.join(" "));
  return out;
}

/**
 * Wyspa WY-1 z linia wysokosci tonu.
 *
 * To NIE jest wykres CH: CH-4 wymaga osi od zera, a os jest logarytmem
 * czestotliwosci 70-400 Hz i 0 Hz nie jest wysokoscia dzwieku. Zamiast lamac
 * regule, ktorej z definicji nie da sie spelnic, stosujemy WY-1 - wlasny "bebech"
 * w oprawie systemu: panel z obrysem, opisana skala (P-4), odczyt liczbowy jako
 * alternatywa tekstowa dla rysunku (A-7) i wlasny stan pusty.
 *
 * SVG idzie na viewBox plus szerokosc 100%, NIGDY sztywne 320px - taka szerokosc
 * nie miescila sie w karcie przy 375px i dawala poziomy scroll calej strony.
 */
function WyspaTonu({ track, f0Hz }: { track: Array<number | null>; f0Hz: number | null }): React.JSX.Element {
  const widoczne = track.slice(-PITCH_POINTS);
  const linie = odcinki(widoczne);
  const odczyt = f0Hz === null ? "brak tonu" : `${Math.round(f0Hz)} Hz`;

  return (
    <section className="mic-wyspa">
      <div className="mic-wyspa__head">
        <h3 className="mic-wyspa__tytul">Wysokość tonu</h3>
        <p className="mic-odczyt">{odczyt}</p>
      </div>

      {linie.length === 0 ? (
        <p className="mic-wyspa__pusto">Jeszcze nie słychać tonu - mów dalej.</p>
      ) : (
        <div className="mic-wykres">
          <div className="mic-skala" aria-hidden="true">
            <span>wysoki</span>
            <span>niski</span>
          </div>
          <svg
            className="mic-pitch"
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Linia wysokości tonu w zakresie od ${PITCH_VIEW_MIN_HZ} do ${PITCH_VIEW_MAX_HZ} Hz. Teraz: ${odczyt}.`}
          >
            {linie.map((punkty, i) => (
              <polyline points={punkty} vectorEffect="non-scaling-stroke" key={i} />
            ))}
          </svg>
        </div>
      )}
    </section>
  );
}

/**
 * Zawartosc karty "Nagranie": przycisk, limit czasu i wyspa tonu.
 *
 * Przycisk NIE jest juz kolem 108px wypelnionym --status-danger-solid. ST-2
 * i DG-1 rezerwuja solidne wypelnienie danger wylacznie dla przycisku
 * potwierdzajacego w dialogu D-4; nagrywanie sygnalizuja ikona stop, etykieta
 * i kropka BD-3 z podpisem. Hero (B-6) tez odpada - jedyne wypelnienie marki
 * w aplikacji jest juz zajete przez akcje uruchamiajaca analize.
 *
 * Stany blokady (brak obslugi, odmowa dostepu) renderuja sie W TEJ KARCIE (S-5)
 * i maja akcje ponowienia - wczesniej jedynym wyjsciem po odmowie bylo
 * przeladowanie strony. Fokus klawiatura dziala, bo prymityw Button ma wspolny
 * :focus-visible; wlasny .mic-button go nie mial (A-1).
 */
export function MicButton({
  state,
  level,
  f0Hz,
  elapsedMs,
  maxSeconds,
  oknoWymiarowS = null,
  pitchTrack,
  onStart,
  onStop,
  analizaMs = 0,
  onPrzerwij,
}: Props): React.JSX.Element {
  if (state === "unsupported") {
    return (
      <StanBledu
        tytul="Nagrywanie niedostępne"
        opis="Ta przeglądarka nie potrafi nagrywać dźwięku. Otwórz stronę w aktualnym Chrome, Firefoksie albo Safari."
      />
    );
  }

  if (state === "denied") {
    return (
      <StanBledu
        tytul="Brak dostępu do mikrofonu"
        opis="Przeglądarka zablokowała mikrofon. Zezwól na dostęp w jej ustawieniach, a potem spróbuj ponownie."
        akcja={
          <Button wariant="secondary" onClick={onStart}>
            Spróbuj ponownie
          </Button>
        }
      />
    );
  }

  if (state === "przerwane") {
    return (
      <StanBledu
        tytul="Nagrywanie zostało przerwane"
        opis="Mikrofon przestał odpowiadać w trakcie nagrywania - zwykle znaczy to, że urządzenie zostało odłączone albo przejęte przez inny program. Sprawdź je i zacznij od nowa."
        akcja={
          <Button wariant="secondary" onClick={onStart}>
            Nagraj jeszcze raz
          </Button>
        }
      />
    );
  }

  const recording = state === "recording";
  const busy = state === "requesting" || state === "processing";
  const pozostaloSek = maxSeconds - elapsedMs / 1000;
  const ostrzezenie = recording && pozostaloSek <= WARN_BEFORE_SEC;

  // Poswiata rosnie z glosnoscia. Skala nieliniowa, zeby cicha mowa tez byla widoczna.
  const skalaPoswiaty = 1 + Math.sqrt(Math.max(0, level)) * 0.12;

  const podpis = (): string => {
    if (state === "requesting") return "Przeglądarka pyta o dostęp do mikrofonu.";
    // Sekundy w podpisie, bo nieme oczekiwanie nie da sie odroznic od zwisu.
    // Zwykle jest to 2-4 s; gdy licznik idzie dalej, uzytkownik widzi problem
    // sam, zamiast dowiadywac sie o nim z braku wyniku po dziewieciu sekundach.
    if (state === "processing") {
      return analizaMs >= 1000
        ? `Analizuję nagranie. Trwa to już ${Math.floor(analizaMs / 1000)} s.`
        : "Analizuję nagranie.";
    }
    if (recording) return "Mów. Kliknij ponownie, żeby zakończyć.";
    // Zdanie o przechowywaniu jest OBOWIAZKOWE, odkad nagranie zostaje na dysku
    // (19.08.2026). Sciezka wgranego nagrania zbiera na to zgode w formularzu,
    // mikrofon nie ma jej gdzie zebrac - wiec zostaje jasny komunikat w miejscu,
    // w ktorym uzytkownik podejmuje decyzje, a nie w regulaminie.
    const oknoModelu =
      oknoWymiarowS !== null && oknoWymiarowS !== undefined
        ? ` Wymiary z sygnału policzą się tylko z nagrania do ${liczbaPl(oknoWymiarowS)} s.`
        : "";
    return `Kliknij i powiedz jedno zdanie. Maksymalnie ${maxSeconds} s.${oknoModelu} Nagranie zostaje na 30 dni, żeby dało się je odsłuchać i porównać modele.`;
  };

  const etykietaPrzycisku = (): string => {
    if (state === "requesting") return "Czekam na mikrofon";
    if (state === "processing") return "Analizuję";
    if (recording) return "Zatrzymaj nagrywanie";
    return "Rozpocznij nagrywanie";
  };

  return (
    <div className="mic-nagranie">
      <div className="mic-akcje">
        <div className="mic-button-wrap">
          <span
            className="mic-ring"
            style={{ transform: `scale(${skalaPoswiaty})`, opacity: recording ? 0.18 + level * 0.32 : 0 }}
            aria-hidden="true"
          />
          <Button wariant="primary" zajety={busy} onClick={recording ? onStop : onStart}>
            {recording ? <IkonaStop /> : <IconMikrofon />}
            {etykietaPrzycisku()}
          </Button>
        </div>

        {/* BD-3: kropka na zywo ZAWSZE z etykieta - kolor sam nie niesie stanu. */}
        {recording && <KropkaNaZywo>Nagrywam</KropkaNaZywo>}

        {/* Wyjscie z czekania. Pojawia sie dopiero po chwili, zeby nie kusilo
            przerywaniem analizy, ktora i tak zaraz sie skonczy. */}
        {state === "processing" && onPrzerwij && analizaMs >= 3000 && (
          <Button wariant="secondary" onClick={onPrzerwij}>
            Przerwij
          </Button>
        )}
      </div>

      <p className="mic-label">{podpis()}</p>

      {recording && (
        <ProgressBar
          etykieta={
            ostrzezenie
              ? `Zostało ${Math.max(0, Math.ceil(pozostaloSek))} s z ${maxSeconds} s`
              : `Nagrywam, limit ${maxSeconds} s`
          }
          procent={Math.min(100, (elapsedMs / 1000 / maxSeconds) * 100)}
        />
      )}

      {(recording || state === "processing") && <WyspaTonu track={pitchTrack} f0Hz={recording ? f0Hz : null} />}
    </div>
  );
}
